(() => {
  const elementName = 'material-variant-selector';

  if (window.customElements.get(elementName)) {
    return;
  }

  const normalize = (value) => String(value ?? '').trim().toLowerCase();

  class MaterialVariantSelector extends HTMLElement {
    connectedCallback() {
      queueMicrotask(() => {
        if (this.isConnected) this.initialize();
      });
    }

    initialize() {
      this.abortController?.abort();
      this.abortController = new AbortController();

      this.formId = this.dataset.formId;
      this.optionPosition = Number(this.dataset.optionPosition);
      this.variantPicker = this.closest('variant-picker');
      this.form = document.forms[this.formId];
      this.trigger = this.querySelector('[data-material-trigger]');
      this.dialog = this.querySelector('[data-material-dialog]');
      this.closeButton = this.querySelector('[data-material-close]');
      this.cards = Array.from(this.querySelectorAll('[data-material-card]'));
      this.selectedMedia = this.querySelector('[data-selected-media]');
      this.selectedValue = this.querySelector('[data-selected-value]');
      this.selectedBadge = this.querySelector('[data-selected-badge]');

      if (!this.formId || !this.optionPosition || !this.variantPicker || !this.form || !this.trigger || !this.dialog) {
        return;
      }

      const { signal } = this.abortController;

      this.trigger.addEventListener('click', () => this.openDialog(), { signal });
      this.closeButton?.addEventListener('click', () => this.closeDialog(), { signal });

      this.dialog.addEventListener('click', (event) => {
        if (event.target === this.dialog) {
          this.closeDialog();
        }
      }, { signal });

      this.dialog.addEventListener('close', () => this.onDialogClosed(), { signal });
      this.dialog.addEventListener('cancel', (event) => {
        event.preventDefault();
        event.stopPropagation();
        this.closeDialog();
      }, { signal });

      window.addEventListener('keydown', (event) => {
        if (this.dialog.open && event.key === 'Escape') {
          event.preventDefault();
          event.stopImmediatePropagation();
          this.closeDialog();
        }
      }, { capture: true, signal });

      this.cards.forEach((card) => {
        if (card instanceof HTMLButtonElement) {
          card.addEventListener('click', () => this.selectMaterial(card), { signal });
        }
      });

      this.variantPicker.addEventListener('change', (event) => {
        const input = event.target;

        if (
          input instanceof HTMLInputElement
          && input.matches('input[data-option-position]')
          && Number(input.dataset.optionPosition) === this.optionPosition
        ) {
          this.syncFromNativeInput(input);
        }
      }, { signal });

      this.form.addEventListener('variant:change', (event) => {
        const variant = event.detail?.variant;

        if (variant?.id && this.syncFromVariantId(variant.id)) {
          return;
        }

        const materialValue = variant?.options?.[this.optionPosition - 1];

        if (materialValue) {
          this.syncFromMaterialName(materialValue);
        }
      }, { signal });

      this.form.addEventListener('change', (event) => {
        const input = event.target;

        if (input instanceof HTMLInputElement && input.name === 'id') {
          this.syncFromVariantId(input.value);
        }
      }, { signal });

      this.syncFromNativeSelection();
    }

    disconnectedCallback() {
      this.restoreFocusOnClose = false;

      if (this.dialog?.open) {
        this.dialog.close();
      }

      this.abortController?.abort();
      this.releasePageScroll();
      this.resumeParentFocusTrap();
    }

    openDialog() {
      if (this.dialog.open) {
        return;
      }

      this.trigger.setAttribute('aria-expanded', 'true');
      document.documentElement.classList.add('material-selector-dialog-open');

      // A native dialog traps its own focus. Pause the quick-buy trap until it closes.
      this.parentDialog = this.closest('quick-buy-modal[open]');
      this.parentFocusTrap = this.parentDialog?.focusTrap;
      this.parentFocusTrap?.pause();

      try {
        this.dialog.showModal();
      } catch (error) {
        this.dialog.setAttribute('open', '');
      }

      requestAnimationFrame(() => {
        if (!this.dialog.open) {
          return;
        }

        const selectedCard = this.cards.find((card) => card.classList.contains('is-selected'));
        const firstAvailableCard = this.cards.find((card) => !card.hasAttribute('disabled'));
        (selectedCard || firstAvailableCard)?.focus();
      });
    }

    closeDialog(restoreFocus = true) {
      this.restoreFocusOnClose = restoreFocus;

      if (this.dialog.open && typeof this.dialog.close === 'function') {
        this.dialog.close();
      } else {
        this.dialog.removeAttribute('open');
        this.onDialogClosed();
      }
    }

    onDialogClosed() {
      this.trigger?.setAttribute('aria-expanded', 'false');
      this.releasePageScroll();
      this.resumeParentFocusTrap();

      if (this.restoreFocusOnClose !== false && this.isConnected) {
        this.trigger?.focus({ preventScroll: true });
      }

      this.restoreFocusOnClose = true;
    }

    resumeParentFocusTrap() {
      if (this.parentDialog?.isConnected && this.parentDialog.open) {
        this.parentFocusTrap?.unpause();
      }
      this.parentDialog = null;
      this.parentFocusTrap = null;
    }

    releasePageScroll() {
      if (!document.querySelector('.material-selector__dialog[open]')) {
        document.documentElement.classList.remove('material-selector-dialog-open');
      }
    }

    selectMaterial(card) {
      if (card.hasAttribute('disabled') || card.getAttribute('aria-disabled') === 'true') {
        return;
      }

      const valueId = card.dataset.materialValueId;
      const nativeInput = Array.from(this.variantPicker.querySelectorAll('input[data-option-position]')).find((input) => (
        Number(input.dataset.optionPosition) === this.optionPosition
        && input.value === valueId
      ));

      if (!nativeInput) {
        return;
      }

      this.setSelectedCard(card);
      this.closeDialog();

      if (!nativeInput.checked) {
        nativeInput.checked = true;
        nativeInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    syncFromNativeSelection() {
      const selectedInput = Array.from(this.variantPicker.querySelectorAll('input[data-option-position]:checked')).find((input) => (
        Number(input.dataset.optionPosition) === this.optionPosition
      ));

      if (selectedInput) {
        this.syncFromNativeInput(selectedInput);
        return;
      }

      const variantIdInput = this.form.querySelector('input[name="id"]');

      if (variantIdInput) {
        this.syncFromVariantId(variantIdInput.value);
      }
    }

    syncFromNativeInput(input) {
      const matchingCard = this.cards.find((card) => card.dataset.materialValueId === input.value);

      if (matchingCard) {
        this.setSelectedCard(matchingCard);
      }
    }

    syncFromVariantId(variantId) {
      const normalizedVariantId = String(variantId);
      const matchingCard = this.cards.find((card) => (
        (card.dataset.variantIds || '').split(/\s+/).filter(Boolean).includes(normalizedVariantId)
      ));

      if (matchingCard) {
        this.setSelectedCard(matchingCard);
        return true;
      }

      return false;
    }

    syncFromMaterialName(materialName) {
      const matchingCard = this.cards.find((card) => (
        normalize(card.dataset.materialValue) === normalize(materialName)
      ));

      if (matchingCard) {
        this.setSelectedCard(matchingCard);
      }
    }

    setSelectedCard(selectedCard) {
      this.cards.forEach((card) => {
        const isSelected = card === selectedCard;
        card.classList.toggle('is-selected', isSelected);

        if (card instanceof HTMLButtonElement) {
          card.setAttribute('aria-pressed', String(isSelected));
        } else {
          card.setAttribute('aria-current', String(isSelected));
        }
      });

      if (this.selectedValue) {
        this.selectedValue.textContent = selectedCard.dataset.materialValue || '';
      }

      const cardBadge = selectedCard.querySelector('.material-selector__card-badge');

      if (this.selectedBadge) {
        this.selectedBadge.textContent = cardBadge?.textContent?.trim() || '';
        this.selectedBadge.hidden = !cardBadge;
      }

      const cardMedia = selectedCard.querySelector('[data-card-media]');
      const mediaElement = cardMedia
        ? Array.from(cardMedia.children).find((child) => (
          child.matches('img, .material-selector__media-placeholder')
        ))
        : null;

      if (this.selectedMedia && mediaElement) {
        const clonedMedia = mediaElement.cloneNode(true);

        if (clonedMedia instanceof HTMLImageElement) {
          clonedMedia.sizes = '56px';
        }

        this.selectedMedia.replaceChildren(clonedMedia);
      }
    }
  }

  window.customElements.define(elementName, MaterialVariantSelector);
})();
